import React, { useEffect } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { useCMSGameCategories } from '../../hooks';
import { LobbyGamesSlideProps, Slide } from './components/Slide/Slide';
import { GameV1 } from '@/api/games';
import { Button } from '@/components/buttons';
import { NativeSlider } from '@/components/NativeSlider';
import { SliderArrow } from '@/components/NativeSlider/helpers';
import { SectionTitle } from '@/components/SectionTitle';
import GamesSkeleton from '@/components/skeletons/GamesSkeleton';
import { createItems } from '@/helpers/common';
import { GameItemMode } from '@/helpers/enums';
import { getGamesPlugs } from '@/helpers/game';
import { getSliderParams } from '@/helpers/getSliderParams';
import { useNavigation } from '@/hooks/navigation';
import { useResponsive } from '@/modules/client';
import { useCommonStore } from '@/store';

import { ColorMode } from '@/components/GameItem/styled';
import { ScGameItemPlugProps } from '@/components/GamesGrid/styled';

interface PerSliderItem {
  cols: number;
  allGames: number;
  rows: number;
  slidesPerView?: number;
}

export type SliderParams = {
  desktopL: PerSliderItem;
  desktop: PerSliderItem;
  tablet: PerSliderItem;
  mobile: PerSliderItem;
  mobileS: PerSliderItem;
};

interface LobbyGamesSliderProps extends Pick<ScGameItemPlugProps, 'plugBlack'> {
  sliderParams: SliderParams;
  categoryId: string;
  subCategoryId?: string;
  title: string | React.ReactElement;
  description?: string;
  titleIcon?: string | React.ReactElement;
  gameItemMode?: GameItemMode;
  lazyLoadingOf?: number;
  titleMultiRow?: boolean;
  darkArrows?: boolean;
  noAllButton?: boolean;
  noArrows?: boolean;
  blackShowAllCard?: boolean;
  gameColorMode?: ColorMode;
  gamesList?: GameV1[];
  plugsOnEmpty?: boolean;
  onGamesLoad?: (games: GameV1[]) => void;
  gamesLength?: number;
}

const LobbyGamesSlider = ({
  sliderParams,
  categoryId,
  titleIcon,
  title,
  description,
  subCategoryId,
  titleMultiRow,
  blackShowAllCard,
  gameColorMode,
  gamesList,
  onGamesLoad,
  darkArrows,
  plugsOnEmpty,
  gamesLength,
  ...props
}: LobbyGamesSliderProps) => {
  const { t } = useTranslation('common');
  const { currentMediaType } = useResponsive();
  const { generateLink } = useNavigation();

  const { categories: gameCategoriesList, isLoading: categoriesPending } =
    useCMSGameCategories();

  const {
    isAuthenticated,
    gamesByCategories,
    pendingGamesByCategories,
    getGamesByCategory,
  } = useCommonStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      gamesByCategories: state.gamesByCategories,
      pendingGamesByCategories: state.pendingGamesByCategories,
      getGamesByCategory: state.getGamesByCategory,
    }))
  );

  const {
    cols,
    rows,
    allGames,
    slidesPerView = 1,
  } = useMemo(
    () => getSliderParams(sliderParams, currentMediaType),
    [currentMediaType, sliderParams]
  );

  const gamesParametr = useMemo(() => {
    if (!gameCategoriesList.length) return '';
    if (subCategoryId) return subCategoryId;

    const gameCategory = gameCategoriesList.find(
      ({ defaultCategoryId }) => defaultCategoryId === categoryId
    );
    if (!gameCategory) return '';

    return gameCategory.id;
  }, [isAuthenticated, gameCategoriesList, subCategoryId, categoryId]);

  useEffect(() => {
    if (
      categoriesPending ||
      !gameCategoriesList ||
      !gamesParametr ||
      gamesByCategories[gamesParametr] ||
      gamesList
    )
      return;

    getGamesByCategory(gamesParametr, {
      pageSize: allGames,
      staticAmount: true,
    });
  }, [
    allGames,
    gamesList,
    gamesParametr,
    getGamesByCategory,
    categoriesPending,
    gameCategoriesList,
    gamesByCategories,
  ]);

  const { games = [], pagination } = useMemo(
    () =>
      gamesList || !gamesByCategories[gamesParametr]
        ? {
            games: gamesList,
            pagination: {
              totalCount: gamesList?.length || 0,
            },
          }
        : gamesByCategories[gamesParametr],
    [gamesByCategories, gamesParametr, gamesList]
  );

  useEffect(() => {
    if (games?.length && onGamesLoad) {
      onGamesLoad(games);
    }
  }, [games, onGamesLoad]);

  const seeAllLink = useMemo(() => {
    const link = subCategoryId
      ? `/games/${categoryId}/sub-category/${subCategoryId}#gamesSection`
      : `/games/${categoryId}#gamesSection`;

    return generateLink(link);
  }, [categoryId, subCategoryId, gameCategoriesList, generateLink]);

  const slides: LobbyGamesSlideProps[] = useMemo(() => {
    const sliderGames =
      pagination.totalCount > allGames ? games.slice(0, allGames) : games;
    const chunkSize = rows * cols;

    const plugs = !sliderGames.length
      ? createItems({}, cols)
      : sliderGames.length <= cols
        ? getGamesPlugs(sliderGames, cols, 1)
        : [];

    if (!sliderGames.length) return [{ plugs, rows, cols, ...props }];

    return Array.from(
      { length: Math.ceil(sliderGames.length / chunkSize) },
      (v, i) => {
        const slideGames = sliderGames.slice(
          i * chunkSize,
          i * chunkSize + chunkSize
        );

        const commonSlideProps = {
          games: slideGames,
          rows,
          cols,
          plugs,
          blackShowAllCard,
          gameColorMode,
          ...props,
        };

        return pagination.totalCount > allGames - 1 &&
          sliderGames.length > chunkSize &&
          i * chunkSize === sliderGames.length - chunkSize
          ? {
              link: seeAllLink,
              ...commonSlideProps,
            }
          : commonSlideProps;
      }
    );
  }, [
    games,
    allGames,
    rows,
    cols,
    props,
    seeAllLink,
    blackShowAllCard,
    gameColorMode,
    pagination,
  ]);
  const loadingGames = useMemo(
    () => categoriesPending || pendingGamesByCategories[gamesParametr],
    [categoriesPending, pendingGamesByCategories, gamesParametr]
  );

  if (!games.length && !plugsOnEmpty && !loadingGames)
    return <GamesSkeleton withTitle />;

  return loadingGames ? (
    <GamesSkeleton withTitle />
  ) : (
    <NativeSlider<LobbyGamesSlideProps>
      slidesPerView={slidesPerView}
      slides={slides}
      slide={Slide}
      arrows={{
        type: SliderArrow.OUTSIDE,
        dark: darkArrows,
        componentLeft: (
          <SectionTitle multiRow={titleMultiRow}>
            <span className="title">
              {title}
              {titleIcon && <span className="after-title">{titleIcon}</span>}
            </span>
            {description && <span className="description">{description}</span>}
          </SectionTitle>
        ),
        componentRight: (
          <Button
            id="gameCategoryButton"
            xSmall
            autoPaddingHorizontal
            to={seeAllLink}
            className="all-btn"
            paddingX={8}
            whiteTransparent
          >
            {`${t('gameCategories.all')} ${gamesLength || pagination.totalCount || 0}`}
          </Button>
        ),
      }}
    />
  );
};

export default LobbyGamesSlider;
